import React, { ReactNode, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { ExTokenRequest, loginSuccess } from '../../redux/auth/action';
import { refreshToken as refreshAuthToken } from '../../service/auth-service/auth-services';

interface AuthGuardProps {
  children: ReactNode;
  allowedRoles?: string[];
}

interface JwtPayload {
  phone: string;
  roles: string[];
  email: string;
  sub: string;
  iat: number;
  exp: number;
}

function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    return jwtDecode<JwtPayload>(token);
  } catch {
    return null;
  }
}

export const isTokenValid = (token: string): boolean => {
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) return false;
  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp > currentTime;
};

const hasAllowedRole = (token: string, allowedRoles: string[]): boolean => {
  if (!allowedRoles.length) return true;
  const payload = decodeJwtPayload(token);
  if (!payload || !Array.isArray(payload.roles)) return false;
  return payload.roles.some((role) => allowedRoles.includes(role));
};

const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  allowedRoles = [],
}: AuthGuardProps) => {
  const dispatch = useDispatch();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      const refreshTokenValue = localStorage.getItem('refreshToken');

      if (token && isTokenValid(token)) {
        setIsAuthenticated(true);
      } else if (token && !isTokenValid(token) && refreshTokenValue) {
        try {
          const response = await refreshAuthToken(refreshTokenValue);
          if (response.status === 200) {
            const { token, refreshToken, user } = response.data;
            localStorage.setItem('token', token);
            localStorage.setItem('refreshToken', refreshToken);
            localStorage.setItem('user', JSON.stringify(user));
            dispatch(loginSuccess({ token, user }));
            setIsAuthenticated(true);
            return;
          }
        } catch (error) {
          console.error('Error refreshing token:', error);
        }
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        dispatch(ExTokenRequest({}));
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, [dispatch]);

  if (isAuthenticated === null) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const token = localStorage.getItem('token');
  if (token && !hasAllowedRole(token, allowedRoles)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

export default AuthGuard;
